// Admin Email — composer / monitor for transactional + broadcast email.
//
// This is the *admin-side* composer (target retailer segments, schedule sends,
// review delivery, manage templates). It is intentionally a broadcast/CRM tool,
// distinct from the IMAP inbox client in the source-of-truth (which lives at
// /var/www/kitchenos/admin-standalone.html:1263). The IMAP inbox is reachable
// via the same backend module (/api/admin/email/inbox|message|send) but this
// page focuses on outbound campaigns + templates + sent log + bounces.
//
// Backend status (as of extraction):
//   POST /api/admin/email/send         — real (modules/email.js → email.js:327)
//   GET  /api/admin/email/accounts     — real (email.js:259) — used here only
//                                        as the dropdown of "from" addresses
//   GET  /api/admin/email/sent         — STUB (no such endpoint today). We
//                                        request it; on 404 we fall back to a
//                                        local example dataset and surface a
//                                        banner.
//   GET  /api/admin/email/templates    — STUB (same fallback strategy)
//   GET  /api/admin/email/bounces      — STUB (same fallback strategy)
//
// All stubbed endpoints fail soft: the page stays usable, a banner explains
// what's stubbed, and ctx.showToast surfaces actionable errors.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type TabId = "compose" | "templates" | "sent" | "bounces";

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: "compose",   label: "Compose" },
  { id: "templates", label: "Templates" },
  { id: "sent",      label: "Sent log" },
  { id: "bounces",   label: "Bounces" },
];

type SegmentId = "all" | "active" | "inactive" | "tier1" | "tier2" | "tier3" | "tier4" | "tier5" | "suspended";

const SEGMENTS: ReadonlyArray<{ id: SegmentId; label: string }> = [
  { id: "all",        label: "All retailers" },
  { id: "active",     label: "Active retailers" },
  { id: "inactive",   label: "Inactive retailers" },
  { id: "tier1",      label: "Tier 1 (3.3%)" },
  { id: "tier2",      label: "Tier 2 (2.8%)" },
  { id: "tier3",      label: "Tier 3 (2.3%)" },
  { id: "tier4",      label: "Tier 4 (1.8%)" },
  { id: "tier5",      label: "Tier 5 (1.5%)" },
  { id: "suspended",  label: "Suspended" },
];

interface EmailAccount {
  id: string;
  email: string;
  label?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  category?: string;
  updated_at?: string;
}

interface SentEmail {
  id: string;
  to: string;
  segment?: string;
  subject: string;
  sent_at: string;
  status: "sent" | "bounced" | "opened" | "queued" | "failed";
  recipients?: number;
  opens?: number;
}

interface BouncedAddress {
  address: string;
  reason: string;
  last_attempt: string;
  count?: number;
}

interface AccountsResponse { ok?: boolean; accounts?: EmailAccount[] }
interface TemplatesResponse { ok?: boolean; templates?: EmailTemplate[] }
interface SentResponse { ok?: boolean; sent?: SentEmail[] }
interface BouncesResponse { ok?: boolean; bounces?: BouncedAddress[] }
interface SendResponse { ok?: boolean; id?: string; queued?: number; error?: string }

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

/**
 * Minimal markdown → HTML for the compose preview. We don't import a
 * dependency; we accept bold, italic, line breaks, headings, and links.
 * Escapes HTML first to avoid injection.
 */
function renderMarkdown(src: string): string {
  let s = esc(src);
  s = s.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  s = s.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  s = s.replace(/^# (.*)$/gm, "<h1>$1</h1>");
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  s = s.replace(
    /\[([^\]]+)\]\(([^\)]+)\)/g,
    (_m, label: string, url: string) => `<a href="${esc(url)}" target="_blank" rel="noopener">${label}</a>`,
  );
  s = s.replace(/\n{2,}/g, "</p><p>");
  s = s.replace(/\n/g, "<br/>");
  return `<p>${s}</p>`;
}

// ── Stub data — used when the backend endpoint doesn't exist yet ─────────────

const STUB_TEMPLATES: EmailTemplate[] = [
  {
    id: "welcome",
    name: "Welcome — new retailer",
    subject: "Welcome to The Kitchen OS",
    category: "Lifecycle",
    body: "Hi {{name}},\n\nWelcome aboard. Your showroom is now live on The Kitchen OS.\n\nNext steps:\n- Connect Stripe so we can route payments\n- Upload your door ranges\n- Invite your team\n\n— The TKOS team",
    updated_at: new Date(Date.now() - 86_400_000 * 4).toISOString(),
  },
  {
    id: "payment-failed",
    name: "Payment failed",
    subject: "We couldn't process a recent payment",
    category: "Billing",
    body: "Hi {{name}},\n\nA recent customer payment to your account failed (reason: {{reason}}). The customer has been notified. No action required from you unless this persists.\n\n— TKOS Billing",
    updated_at: new Date(Date.now() - 86_400_000 * 12).toISOString(),
  },
  {
    id: "tier-upgrade",
    name: "Tier upgrade",
    subject: "You've moved up a tier",
    category: "Loyalty",
    body: "Congratulations {{name}} — your rolling 12-month turnover has pushed you up to **{{tier}}**. Your commission rate drops to **{{rate}}%** from today.\n\nKeep going.",
    updated_at: new Date(Date.now() - 86_400_000 * 22).toISOString(),
  },
  {
    id: "downtime",
    name: "Scheduled maintenance",
    subject: "Scheduled maintenance — {{date}}",
    category: "Ops",
    body: "Hi {{name}},\n\nWe'll be doing scheduled maintenance on {{date}} between {{start}} and {{end}}. Expect brief interruptions.\n\n— TKOS Ops",
    updated_at: new Date(Date.now() - 86_400_000 * 60).toISOString(),
  },
];

const STUB_SENT: SentEmail[] = [
  { id: "s1", to: "active retailers", segment: "active", subject: "Quarterly product update", sent_at: new Date(Date.now() - 86_400_000 * 2).toISOString(), status: "sent", recipients: 132, opens: 79 },
  { id: "s2", to: "tier1@kitchencompany.co.uk", subject: "Tier upgrade — welcome to Tier 2", sent_at: new Date(Date.now() - 86_400_000 * 5).toISOString(), status: "opened", recipients: 1, opens: 1 },
  { id: "s3", to: "all", segment: "all", subject: "Scheduled maintenance — Tue 14th", sent_at: new Date(Date.now() - 86_400_000 * 8).toISOString(), status: "sent", recipients: 184, opens: 96 },
  { id: "s4", to: "ops@sjbkitchens.example", subject: "Stripe rotation reminder", sent_at: new Date(Date.now() - 86_400_000 * 10).toISOString(), status: "bounced", recipients: 1, opens: 0 },
];

const STUB_BOUNCES: BouncedAddress[] = [
  { address: "ops@sjbkitchens.example", reason: "Mailbox does not exist (550)", last_attempt: new Date(Date.now() - 86_400_000 * 10).toISOString(), count: 3 },
  { address: "noreply@deadcompany.co.uk", reason: "Domain not found (NXDOMAIN)", last_attempt: new Date(Date.now() - 86_400_000 * 18).toISOString(), count: 5 },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const emailPage: Page = {
  name: "email",
  label: "Email",
  icon: "✉️",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();

    // ── State ──────────────────────────────────────────────────────────────
    let activeTab: TabId = "compose";

    // Compose state
    let composeSegment: SegmentId = "active";
    let composeFromId = "";
    let composeSubject = "";
    let composeBody = "";
    let composeTestTo = "";
    let composeScheduleAt = "";
    let composeShowPreview = false;
    let composeBusy = false;

    let accounts: EmailAccount[] = [];
    let templates: EmailTemplate[] | null = null;
    let templatesStubbed = false;
    let sent: SentEmail[] | null = null;
    let sentStubbed = false;
    let bounces: BouncedAddress[] | null = null;
    let bouncesStubbed = false;

    let editingTemplate: EmailTemplate | null = null;

    // ── DOM scaffold ──────────────────────────────────────────────────────
    const root = document.createElement("section");
    root.className = "aem-page";
    root.innerHTML = `
      <header class="aem-header">
        <div>
          <h1>Email</h1>
          <p>Transactional and broadcast email — compose, manage templates, review delivery.</p>
        </div>
      </header>

      <nav class="aem-tabs" role="tablist" aria-label="Email sections">
        ${TABS.map((t) => `
          <button
            type="button"
            class="aem-tab"
            role="tab"
            data-tab="${t.id}"
            aria-selected="${t.id === activeTab ? "true" : "false"}"
          >${esc(t.label)}</button>
        `).join("")}
      </nav>

      <div class="aem-content" data-role="content" role="tabpanel"></div>

      <dialog data-role="template-dialog" class="aem-dialog">
        <form method="dialog" class="aem-dialog__form">
          <h2 data-role="dialog-title">Template</h2>
          <label class="aem-field">
            <span>Name</span>
            <input class="adm-input" data-role="tpl-name" type="text" required />
          </label>
          <label class="aem-field">
            <span>Subject</span>
            <input class="adm-input" data-role="tpl-subject" type="text" required />
          </label>
          <label class="aem-field">
            <span>Body (Markdown)</span>
            <textarea class="adm-textarea" data-role="tpl-body" rows="10"></textarea>
          </label>
          <div class="aem-dialog__actions">
            <button type="button" class="adm-btn adm-btn-ghost" data-role="tpl-cancel">Cancel</button>
            <button type="button" class="adm-btn adm-btn-ghost" data-role="tpl-test">Send test</button>
            <button type="button" class="adm-btn" data-role="tpl-save">Save</button>
          </div>
        </form>
      </dialog>
    `;
    container.replaceChildren(root);

    // ── DOM refs ──────────────────────────────────────────────────────────
    const contentEl = root.querySelector<HTMLDivElement>("[data-role='content']")!;
    const dialogEl = root.querySelector<HTMLDialogElement>("[data-role='template-dialog']")!;
    const tplName = dialogEl.querySelector<HTMLInputElement>("[data-role='tpl-name']")!;
    const tplSubject = dialogEl.querySelector<HTMLInputElement>("[data-role='tpl-subject']")!;
    const tplBody = dialogEl.querySelector<HTMLTextAreaElement>("[data-role='tpl-body']")!;
    const tplCancel = dialogEl.querySelector<HTMLButtonElement>("[data-role='tpl-cancel']")!;
    const tplTest = dialogEl.querySelector<HTMLButtonElement>("[data-role='tpl-test']")!;
    const tplSave = dialogEl.querySelector<HTMLButtonElement>("[data-role='tpl-save']")!;
    const dialogTitle = dialogEl.querySelector<HTMLHeadingElement>("[data-role='dialog-title']")!;

    // ── Backend calls ─────────────────────────────────────────────────────

    async function fetchAccounts(): Promise<void> {
      try {
        const resp = await ctx.api<AccountsResponse>("/admin/email/accounts", {
          method: "GET",
          signal: ac.signal,
        });
        accounts = Array.isArray(resp.accounts) ? resp.accounts : [];
        if (!composeFromId && accounts[0]) composeFromId = accounts[0].id;
      } catch (err) {
        if (isAbortError(err)) return;
        accounts = [];
        if (!isNotFound(err)) {
          console.warn("[admin/email] /admin/email/accounts failed:", err);
        }
      }
    }

    async function fetchTemplates(): Promise<void> {
      try {
        const resp = await ctx.api<TemplatesResponse>("/admin/email/templates", {
          method: "GET",
          signal: ac.signal,
        });
        templates = Array.isArray(resp.templates) ? resp.templates : [];
        templatesStubbed = false;
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          templates = [...STUB_TEMPLATES];
          templatesStubbed = true;
          return;
        }
        templates = [];
        console.warn("[admin/email] /admin/email/templates failed:", err);
      }
    }

    async function fetchSent(): Promise<void> {
      try {
        const resp = await ctx.api<SentResponse>("/admin/email/sent", {
          method: "GET",
          signal: ac.signal,
        });
        sent = Array.isArray(resp.sent) ? resp.sent : [];
        sentStubbed = false;
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          sent = [...STUB_SENT];
          sentStubbed = true;
          return;
        }
        sent = [];
        console.warn("[admin/email] /admin/email/sent failed:", err);
      }
    }

    async function fetchBounces(): Promise<void> {
      try {
        const resp = await ctx.api<BouncesResponse>("/admin/email/bounces", {
          method: "GET",
          signal: ac.signal,
        });
        bounces = Array.isArray(resp.bounces) ? resp.bounces : [];
        bouncesStubbed = false;
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          bounces = [...STUB_BOUNCES];
          bouncesStubbed = true;
          return;
        }
        bounces = [];
        console.warn("[admin/email] /admin/email/bounces failed:", err);
      }
    }

    async function sendCompose(opts: { test?: boolean } = {}): Promise<void> {
      if (composeBusy) return;
      const subject = composeSubject.trim();
      const body = composeBody.trim();
      if (!subject) {
        ctx.showToast("Subject is required", "error");
        return;
      }
      if (!body) {
        ctx.showToast("Body is required", "error");
        return;
      }
      const from = accounts.find((a) => a.id === composeFromId);
      if (!from) {
        ctx.showToast("Pick a sender address", "error");
        return;
      }
      if (opts.test && !composeTestTo.trim()) {
        ctx.showToast("Enter a test recipient", "error");
        return;
      }

      composeBusy = true;
      renderContent();

      const payload: Record<string, unknown> = {
        from: from.email,
        subject,
        text: body,
        html: renderMarkdown(body),
      };

      if (opts.test) {
        payload["to"] = composeTestTo.trim();
        payload["test"] = true;
      } else {
        payload["segment"] = composeSegment;
        if (composeScheduleAt) payload["scheduleAt"] = composeScheduleAt;
      }

      try {
        const resp = await ctx.api<SendResponse>("/admin/email/send", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify(payload),
        });
        if (resp.ok === false) throw new Error(resp.error || "Send failed");
        const queued = resp.queued ?? (opts.test ? 1 : undefined);
        const msg = opts.test
          ? "Test email sent"
          : composeScheduleAt
          ? "Scheduled for delivery"
          : queued
          ? `Queued for ${queued} recipient${queued === 1 ? "" : "s"}`
          : "Email queued";
        ctx.showToast(msg, "success");
        if (!opts.test) {
          composeSubject = "";
          composeBody = "";
          composeScheduleAt = "";
        }
      } catch (err) {
        if (isAbortError(err)) return;
        const msg = err instanceof Error ? err.message : "Send failed";
        ctx.showToast(msg, "error");
      } finally {
        composeBusy = false;
        renderContent();
      }
    }

    // ── Render: tab strip aria ────────────────────────────────────────────
    function refreshTabStrip(): void {
      for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>(".aem-tab"))) {
        btn.setAttribute("aria-selected", btn.dataset["tab"] === activeTab ? "true" : "false");
      }
    }

    // ── Render: content ───────────────────────────────────────────────────
    function renderContent(): void {
      refreshTabStrip();
      if (activeTab === "compose") {
        renderCompose();
        return;
      }
      if (activeTab === "templates") {
        renderTemplates();
        return;
      }
      if (activeTab === "sent") {
        renderSent();
        return;
      }
      renderBounces();
    }

    function renderCompose(): void {
      const fromOpts = accounts.length === 0
        ? `<option value="">— No accounts found —</option>`
        : accounts.map((a) => `
            <option value="${esc(a.id)}" ${a.id === composeFromId ? "selected" : ""}>
              ${esc(a.label ?? a.email)} — ${esc(a.email)}
            </option>
          `).join("");

      const segOpts = SEGMENTS.map((s) => `
        <option value="${s.id}" ${s.id === composeSegment ? "selected" : ""}>${esc(s.label)}</option>
      `).join("");

      const preview = composeShowPreview
        ? `<div class="aem-preview" data-role="preview"><div class="aem-preview__pill">PREVIEW</div>${renderMarkdown(composeBody || "_Nothing to preview yet._")}</div>`
        : "";

      contentEl.innerHTML = `
        <div class="aem-card">
          <div class="aem-row">
            <label class="aem-field">
              <span>From</span>
              <select class="adm-select" data-role="from">${fromOpts}</select>
            </label>
            <label class="aem-field">
              <span>Audience</span>
              <select class="adm-select" data-role="segment">${segOpts}</select>
            </label>
          </div>

          <label class="aem-field">
            <span>Subject</span>
            <input class="adm-input" data-role="subject" type="text" value="${esc(composeSubject)}" placeholder="Your subject line" />
          </label>

          <label class="aem-field">
            <span>Body — Markdown supported (**bold**, *italic*, # headings, [link](https://…))</span>
            <textarea class="adm-textarea aem-body" data-role="body" rows="12" placeholder="Write your message…">${esc(composeBody)}</textarea>
          </label>

          <div class="aem-row">
            <label class="aem-field">
              <span>Test send to</span>
              <input class="adm-input" data-role="test-to" type="email" value="${esc(composeTestTo)}" placeholder="my@email.example" />
            </label>
            <label class="aem-field">
              <span>Schedule send (optional)</span>
              <input class="adm-input" data-role="schedule-at" type="datetime-local" value="${esc(composeScheduleAt)}" />
            </label>
          </div>

          <div class="aem-actions">
            <button type="button" class="adm-btn adm-btn-ghost" data-role="toggle-preview">
              ${composeShowPreview ? "Hide preview" : "Preview"}
            </button>
            <button type="button" class="adm-btn adm-btn-ghost" data-role="send-test" ${composeBusy ? "disabled" : ""}>
              Send test
            </button>
            <button type="button" class="adm-btn" data-role="send" ${composeBusy ? "disabled" : ""}>
              ${composeBusy ? "Sending…" : composeScheduleAt ? "Schedule send" : "Send to segment"}
            </button>
          </div>

          ${preview}
        </div>
      `;
    }

    function renderTemplates(): void {
      if (!templates) {
        contentEl.innerHTML = `<div class="adm-loading">Loading templates…</div>`;
        return;
      }
      const banner = templatesStubbed
        ? `<div class="aem-banner">Showing example templates — <code>/api/admin/email/templates</code> not wired yet.</div>`
        : "";
      const items = templates.length === 0
        ? `<div class="adm-empty">No templates yet.</div>`
        : templates.map((t) => `
            <article class="aem-tpl" data-id="${esc(t.id)}">
              <div class="aem-tpl__main">
                <div class="aem-tpl__name">${esc(t.name)}</div>
                <div class="aem-tpl__subject">${esc(t.subject)}</div>
                <div class="aem-tpl__meta">
                  ${t.category ? `<span class="adm-pill">${esc(t.category)}</span>` : ""}
                  ${t.updated_at ? `<span class="aem-tpl__updated">Updated ${esc(fmtDate(t.updated_at))}</span>` : ""}
                </div>
              </div>
              <div class="aem-tpl__actions">
                <button type="button" class="adm-btn adm-btn-ghost" data-tpl-action="edit" data-id="${esc(t.id)}">Edit</button>
                <button type="button" class="adm-btn adm-btn-ghost" data-tpl-action="duplicate" data-id="${esc(t.id)}">Duplicate</button>
                <button type="button" class="adm-btn adm-btn-ghost" data-tpl-action="test" data-id="${esc(t.id)}">Test send</button>
                <button type="button" class="adm-btn adm-btn-ghost" data-tpl-action="use" data-id="${esc(t.id)}">Use in compose</button>
              </div>
            </article>
          `).join("");

      contentEl.innerHTML = `
        ${banner}
        <div class="aem-tpl-toolbar">
          <button type="button" class="adm-btn" data-role="new-template">+ New template</button>
        </div>
        <div class="aem-tpl-list">${items}</div>
      `;
    }

    function renderSent(): void {
      if (!sent) {
        contentEl.innerHTML = `<div class="adm-loading">Loading sent log…</div>`;
        return;
      }
      const banner = sentStubbed
        ? `<div class="aem-banner">Showing example data — <code>/api/admin/email/sent</code> not wired yet.</div>`
        : "";
      const rows = sent.length === 0
        ? `<tr><td colspan="5" class="adm-empty">No emails sent yet.</td></tr>`
        : sent.map((s) => {
            const statusClass = s.status === "bounced" || s.status === "failed"
              ? "adm-pill-danger"
              : s.status === "opened"
              ? "adm-pill-ok"
              : s.status === "queued"
              ? "adm-pill-info"
              : "adm-pill";
            return `
              <tr>
                <td>${esc(s.to)}</td>
                <td>${esc(s.subject)}</td>
                <td>${esc(fmtDate(s.sent_at))}</td>
                <td><span class="adm-pill ${statusClass}">${esc(s.status)}</span></td>
                <td>${s.recipients ?? 1}${typeof s.opens === "number" ? ` <span class="aem-opens">(${s.opens} opens)</span>` : ""}</td>
              </tr>
            `;
          }).join("");

      contentEl.innerHTML = `
        ${banner}
        <div class="aem-card aem-card--flush">
          <table class="adm-table">
            <thead>
              <tr><th>To</th><th>Subject</th><th>Sent</th><th>Status</th><th>Count</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
    }

    function renderBounces(): void {
      if (!bounces) {
        contentEl.innerHTML = `<div class="adm-loading">Loading bounces…</div>`;
        return;
      }
      const banner = bouncesStubbed
        ? `<div class="aem-banner">Showing example data — <code>/api/admin/email/bounces</code> not wired yet.</div>`
        : "";
      const rows = bounces.length === 0
        ? `<tr><td colspan="4" class="adm-empty">No bounces. Good inbox hygiene.</td></tr>`
        : bounces.map((b) => `
            <tr>
              <td>${esc(b.address)}</td>
              <td>${esc(b.reason)}</td>
              <td>${esc(fmtDate(b.last_attempt))}</td>
              <td>${b.count ?? 1}</td>
            </tr>
          `).join("");

      contentEl.innerHTML = `
        ${banner}
        <div class="aem-card aem-card--flush">
          <table class="adm-table">
            <thead>
              <tr><th>Address</th><th>Reason</th><th>Last attempt</th><th>Bounces</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
    }

    // ── Tab switching ─────────────────────────────────────────────────────
    function switchTab(id: TabId): void {
      activeTab = id;
      renderContent();
      // Lazy load each tab's data the first time it's opened.
      if (id === "templates" && !templates) {
        void fetchTemplates().then(renderContent);
      } else if (id === "sent" && !sent) {
        void fetchSent().then(renderContent);
      } else if (id === "bounces" && !bounces) {
        void fetchBounces().then(renderContent);
      }
    }

    // ── Template dialog ───────────────────────────────────────────────────
    function openTemplateDialog(tpl: EmailTemplate | null): void {
      editingTemplate = tpl;
      dialogTitle.textContent = tpl ? `Edit — ${tpl.name}` : "New template";
      tplName.value = tpl?.name ?? "";
      tplSubject.value = tpl?.subject ?? "";
      tplBody.value = tpl?.body ?? "";
      if (typeof dialogEl.showModal === "function") {
        try { dialogEl.showModal(); } catch { dialogEl.setAttribute("open", ""); }
      } else {
        dialogEl.setAttribute("open", "");
      }
    }

    function closeTemplateDialog(): void {
      editingTemplate = null;
      if (dialogEl.open) {
        try { dialogEl.close(); } catch { /* ignore */ }
      }
      dialogEl.removeAttribute("open");
    }

    async function saveTemplate(): Promise<void> {
      const name = tplName.value.trim();
      const subject = tplSubject.value.trim();
      const body = tplBody.value;
      if (!name || !subject) {
        ctx.showToast("Name and subject are required", "error");
        return;
      }
      // Try the real endpoint; on 404 we keep the change in-memory and warn.
      const isEdit = !!editingTemplate;
      const payload = { id: editingTemplate?.id, name, subject, body };
      try {
        const path = isEdit ? `/admin/email/templates/${editingTemplate!.id}` : "/admin/email/templates";
        await ctx.api<{ ok?: boolean }>(path, {
          method: isEdit ? "PUT" : "POST",
          signal: ac.signal,
          body: JSON.stringify(payload),
        });
        await fetchTemplates();
        ctx.showToast(isEdit ? "Template updated" : "Template created", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          // In-memory mutation only.
          if (!templates) templates = [];
          if (isEdit && editingTemplate) {
            templates = templates.map((t) =>
              t.id === editingTemplate!.id ? { ...t, name, subject, body, updated_at: new Date().toISOString() } : t,
            );
          } else {
            templates = [
              { id: `local-${Date.now()}`, name, subject, body, updated_at: new Date().toISOString() },
              ...templates,
            ];
            templatesStubbed = true;
          }
          ctx.showToast(`${isEdit ? "Updated" : "Created"} (local only — endpoint not wired)`, "info");
        } else {
          ctx.showToast(err instanceof Error ? err.message : "Save failed", "error");
        }
      }
      closeTemplateDialog();
      renderContent();
    }

    function useTemplateInCompose(tpl: EmailTemplate): void {
      composeSubject = tpl.subject;
      composeBody = tpl.body;
      activeTab = "compose";
      renderContent();
      ctx.showToast(`Loaded "${tpl.name}" into composer`, "info");
    }

    async function sendTemplateTest(tpl: EmailTemplate): Promise<void> {
      const to = window.prompt(`Send test of "${tpl.name}" to which address?`, composeTestTo);
      if (!to) return;
      const from = accounts.find((a) => a.id === composeFromId) ?? accounts[0];
      if (!from) {
        ctx.showToast("No sender account available", "error");
        return;
      }
      try {
        await ctx.api<SendResponse>("/admin/email/send", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify({
            from: from.email,
            to: to.trim(),
            subject: tpl.subject,
            text: tpl.body,
            html: renderMarkdown(tpl.body),
            test: true,
            templateId: tpl.id,
          }),
        });
        ctx.showToast("Test sent", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast(err instanceof Error ? err.message : "Test send failed", "error");
      }
    }

    // ── Event delegation on the content area ──────────────────────────────
    const onTabClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".aem-tab");
      if (!btn) return;
      const id = btn.dataset["tab"] as TabId | undefined;
      if (id) switchTab(id);
    };

    const onContentInput = (e: Event): void => {
      const t = e.target as HTMLElement | null;
      if (!t || !(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement)) return;
      const role = t.dataset["role"];
      if (role === "from") composeFromId = t.value;
      else if (role === "segment") composeSegment = t.value as SegmentId;
      else if (role === "subject") composeSubject = t.value;
      else if (role === "body") composeBody = t.value;
      else if (role === "test-to") composeTestTo = t.value;
      else if (role === "schedule-at") composeScheduleAt = t.value;
    };

    const onContentClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLButtonElement>("button");
      if (!btn) return;
      const role = btn.dataset["role"];

      if (activeTab === "compose") {
        if (role === "send") { void sendCompose(); return; }
        if (role === "send-test") { void sendCompose({ test: true }); return; }
        if (role === "toggle-preview") {
          composeShowPreview = !composeShowPreview;
          renderContent();
          return;
        }
      }

      if (activeTab === "templates") {
        if (role === "new-template") { openTemplateDialog(null); return; }
        const action = btn.dataset["tplAction"];
        const id = btn.dataset["id"];
        if (action && id && templates) {
          const tpl = templates.find((t) => t.id === id);
          if (!tpl) return;
          if (action === "edit") { openTemplateDialog(tpl); return; }
          if (action === "duplicate") {
            templates = [
              { ...tpl, id: `local-${Date.now()}`, name: `${tpl.name} (copy)`, updated_at: new Date().toISOString() },
              ...templates,
            ];
            renderContent();
            ctx.showToast("Duplicated (local)", "info");
            return;
          }
          if (action === "test") { void sendTemplateTest(tpl); return; }
          if (action === "use") { useTemplateInCompose(tpl); return; }
        }
      }
    };

    // Tab strip click is on root, content events on contentEl.
    root.querySelector<HTMLElement>(".aem-tabs")!.addEventListener("click", onTabClick);
    contentEl.addEventListener("click", onContentClick);
    contentEl.addEventListener("input", onContentInput);
    contentEl.addEventListener("change", onContentInput);

    // Dialog wiring
    const onDialogCancel = (): void => closeTemplateDialog();
    const onDialogSave = (): void => { void saveTemplate(); };
    const onDialogTest = (): void => {
      // Build a transient template object from the current dialog fields.
      const transient: EmailTemplate = {
        id: editingTemplate?.id ?? "transient",
        name: tplName.value.trim() || "Untitled",
        subject: tplSubject.value.trim(),
        body: tplBody.value,
      };
      void sendTemplateTest(transient);
    };
    tplCancel.addEventListener("click", onDialogCancel);
    tplSave.addEventListener("click", onDialogSave);
    tplTest.addEventListener("click", onDialogTest);

    const onDialogEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape") closeTemplateDialog();
    };
    dialogEl.addEventListener("keydown", onDialogEscape);

    // ── Boot ──────────────────────────────────────────────────────────────
    renderContent();
    void fetchAccounts().then(renderContent);

    // ── Cleanup ───────────────────────────────────────────────────────────
    return () => {
      ac.abort();
      root.querySelector<HTMLElement>(".aem-tabs")?.removeEventListener("click", onTabClick);
      contentEl.removeEventListener("click", onContentClick);
      contentEl.removeEventListener("input", onContentInput);
      contentEl.removeEventListener("change", onContentInput);
      tplCancel.removeEventListener("click", onDialogCancel);
      tplSave.removeEventListener("click", onDialogSave);
      tplTest.removeEventListener("click", onDialogTest);
      dialogEl.removeEventListener("keydown", onDialogEscape);
      if (dialogEl.open) {
        try { dialogEl.close(); } catch { /* ignore */ }
      }
    };
  },
};

registerPage(emailPage);

export default emailPage;
